import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { TrendingUp, Calendar, UserCheck, Archive } from "lucide-react";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useLanguage } from "@/contexts/LanguageContext";

interface AnalyticsState {
  interviews: number;
  hired: number;
  archived: number;
}

export const HiringAnalytics = () => {
  const { t } = useLanguage();
  const [rangeDays, setRangeDays] = useState<string>("30");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AnalyticsState>({ interviews: 0, hired: 0, archived: 0 });
  const { isSuperAdmin, isAdmin, isUnassignedAdmin, adminTeamIds } = useUserRoles();

  const ranges = [
    { label: t('last30Days'), value: "30" },
    { label: t('last90Days'), value: "90" },
    { label: t('last12Months'), value: "365" },
  ];

  const { fromISO, toISO } = useMemo(() => {
    const days = Number(rangeDays || 30);
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - days);
    return { fromISO: from.toISOString(), toISO: to.toISOString() };
  }, [rangeDays]);

  const fetchData = async () => {
    try {
      setLoading(true);

      console.log('[HiringAnalytics] Fetching data - isSuperAdmin:', isSuperAdmin, 'isAdmin:', isAdmin, 'isUnassignedAdmin:', isUnassignedAdmin, 'adminTeamIds:', adminTeamIds);

      // Unassigned admins see no data
      if (isUnassignedAdmin) {
        setData({ interviews: 0, hired: 0, archived: 0 });
        setLoading(false);
        return;
      }

      // Get allowed creator IDs for team admins
      let allowedCreatorIds: string[] | null = null;
      if (isAdmin && !isSuperAdmin && adminTeamIds.length > 0) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Get all managers in the admin's teams
          const { data: teamManagers } = await supabase
            .from('manager_teams')
            .select('manager_id')
            .in('team_id', adminTeamIds);

          const managerIds = teamManagers?.map(m => m.manager_id) || [];
          allowedCreatorIds = [user.id, ...managerIds];
          console.log('[HiringAnalytics] Team admin filtering - User ID:', user.id, 'Allowed creator IDs:', allowedCreatorIds);
        }
      }

      // Interviews completed in range
      const { count: interviews } = await supabase
        .from("interviews")
        .select("*", { count: "exact", head: true })
        .gte("created_at", fromISO)
        .lte("created_at", toISO)
        .eq("status", "completed");

      // Hired candidates in range (filtered by creator for team admins)
      let hiredQuery = supabase
        .from("candidates")
        .select("*", { count: "exact", head: true })
        .gte("updated_at", fromISO)
        .lte("updated_at", toISO)
        .eq("status", "hired");

      if (allowedCreatorIds) {
        console.log('[HiringAnalytics] Filtering hired by creator IDs:', allowedCreatorIds);
        hiredQuery = hiredQuery.in("created_by", allowedCreatorIds);
      }

      const { count: hired } = await hiredQuery;
      console.log('[HiringAnalytics] Hired count:', hired, 'Filtered:', !!allowedCreatorIds);

      // Archived candidates in range (filtered by creator for team admins)
      let archivedQuery = supabase
        .from("candidates")
        .select("*", { count: "exact", head: true })
        .gte("updated_at", fromISO)
        .lte("updated_at", toISO)
        .eq("status", "archived");

      if (allowedCreatorIds) {
        archivedQuery = archivedQuery.in("created_by", allowedCreatorIds);
      }

      const { count: archived } = await archivedQuery;

      setData({ interviews: interviews || 0, hired: hired || 0, archived: archived || 0 });
    } catch (e) {
      console.error("Hiring analytics fetch error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDays, isSuperAdmin, isAdmin, isUnassignedAdmin, adminTeamIds]);

  const successRate = useMemo(() => {
    if (!data.interviews) return 0;
    return Math.round((data.hired / data.interviews) * 100);
  }, [data]);

  return (
    <section className="mt-10 space-y-4" aria-label="Hiring analytics">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('hiringAnalytics')}</h2>
        <Select value={rangeDays} onValueChange={setRangeDays}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder={t('selectRange')} />
          </SelectTrigger>
          <SelectContent>
            {ranges.map(r => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-background/95 via-background/90 to-blue-50/50 border-blue-200/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">{t('totalInterviews')}</CardTitle>
            <Calendar className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? "–" : data.interviews}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-background/95 via-background/90 to-emerald-50/50 border-emerald-200/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">{t('hired')}</CardTitle>
            <UserCheck className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? "–" : data.hired}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-background/95 via-background/90 to-amber-50/50 border-amber-200/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">{t('archived')}</CardTitle>
            <Archive className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? "–" : data.archived}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-background/95 via-background/90 to-indigo-50/50 border-indigo-200/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">{t('successRate')}</CardTitle>
            <TrendingUp className="h-4 w-4 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? "–" : `${successRate}%`}</div>
          </CardContent>
        </Card>
      </div>

      {/* Table summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('periodStatistics')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="py-2 pr-4">{t('metric')}</th>
                  <th className="py-2 pr-4">{t('value')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2 pr-4">{t('totalInterviewsCompleted')}</td>
                  <td className="py-2 pr-4">{data.interviews}</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4">{t('hired')}</td>
                  <td className="py-2 pr-4">{data.hired}</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4">{t('archived')}</td>
                  <td className="py-2 pr-4">{data.archived}</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4">{t('successRate')}</td>
                  <td className="py-2 pr-4">{`${successRate}%`}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{t('range')}: {t('last30Days').toLowerCase().replace('τελευταίες ', '').replace('last ', '')} {rangeDays} {t('days')}</p>
        </CardContent>
      </Card>
    </section>
  );
};

export default HiringAnalytics;
